import { type ReactNode } from "react";

import { BdsText } from "@buildertrend/components";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { MultiSelectFieldEditor } from "commonComponents/entity/customField/utils/customFieldTypes/MultiSelectFieldEditor";
import {
    getOptionTexts,
    parseCommaSeparatedIds,
} from "commonComponents/entity/customField/utils/customFieldTypes/selectCustomFieldUtils";
import { UserSubCellEditor } from "commonComponents/entity/customField/utils/customFieldTypes/UserSubCellEditor";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

import {
    type CustomFieldAssociatedType,
    type MultiSelectUsersCustomFieldDefinition,
    type MultiSelectUsersCustomFieldValue,
} from "handlers";

export const MultiSelectUsersCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    MultiSelectUsersCustomFieldValue,
    MultiSelectUsersCustomFieldDefinition
> = {
    getValue(field): string | null {
        const validIds = field.selectedUserIds?.filter((id) => id > 0);
        return validIds?.length ? validIds.join(",") : "";
    },

    setValue(field, value): MultiSelectUsersCustomFieldValue {
        const ids = value
            ? value
                  .split(",")
                  .map(Number)
                  .filter((n) => !isNaN(n))
            : [];
        return { ...field, selectedUserIds: ids };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): MultiSelectUsersCustomFieldValue {
        return {
            dataType: "MultiSelectUsers",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            selectedUserIds: value
                ? value
                      .split(",")
                      .map(Number)
                      .filter((n) => !isNaN(n))
                : [],
        };
    },

    compare(valueA: string | null, valueB: string | null, definition?): number {
        const idsA = valueA ? parseCommaSeparatedIds(valueA) : [];
        const idsB = valueB ? parseCommaSeparatedIds(valueB) : [];
        const textA = getOptionTexts(idsA, definition);
        const textB = getOptionTexts(idsB, definition);
        return textA.localeCompare(textB, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: string | null, definition): ReactNode {
        if (!value) {
            return <BdsText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const ids = parseCommaSeparatedIds(value);
        if (ids.length === 0) {
            return <BdsText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const displayText = getOptionTexts(ids, definition);
        return <BdsText size="normal-md" text={displayText} />;
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string | null) => void,
        onBlur: () => void,
        _autoFocus?: boolean,
        definition?: MultiSelectUsersCustomFieldDefinition,
        _field?: MultiSelectUsersCustomFieldValue,
        formMode?: "always-editing" | "click-to-edit",
    ): ReactNode {
        return (
            <MultiSelectFieldEditor
                customFieldId={customFieldId}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                definition={definition}
                testId="multiSelectUsersCustomFieldEditor"
                formMode={formMode}
            />
        );
    },

    getAgGridCellEditor() {
        return UserSubCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
